import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import "./ItemsPage.css";
import Pagination from "./Pagination";
import {BASE_URL} from "./Constants";

const ItemsPage = () => {
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [selectedItem, setSelectedItem] = useState(null);
  const [isEditing, setIsEditing] = useState(false);

  /* ================= PAGINATION ================= */
  const [page, setPage] = useState(1);
  const pageSize = 10;
  const [totalCount, setTotalCount] = useState(0);

  /* ================= FORM ================= */
  const [form, setForm] = useState({
    name: "",
    category: "",
    parent_category: "",
    sku_no: "",
    quantity: "",
    price: "",
    brand: "",
    package_details: "",
    description: "",
    gst_slabs_tax_class: "",
  });

  const [tags, setTags] = useState([]);
  const [tagInput, setTagInput] = useState("");

  const [features, setFeatures] = useState([""]);
  const [specifications, setSpecifications] = useState([""]);
  const [warranty_information, setWarrantyInformation] = useState([""]);
  const [user, setUser] = useState(null);

  /* ================= FILES ================= */
  const [images, setImages] = useState([]); // selected images (File objects)
  const [videos, setVideos] = useState([]); // selected video
  const [attachments, setAttachments] = useState([]); // selected attachment

  const [imageList, setImageList] = useState([]); // uploaded images from backend
  const [videoUrl, setVideoUrl] = useState(null); // uploaded video from backend
  const [attachmentList, setAttachmentList] = useState([]); // uploaded attachments from backend

  const [searchQuery, setSearchQuery] = useState("");
  const [isSearching, setIsSearching] = useState(false);

  const [message, setMessage] = useState(null);

  const normalizeArray = (value) => {
    if (Array.isArray(value)) return value;
    if (!value) return [""];
    if (typeof value === "string") {
      try {
        const parsed = JSON.parse(value);
        return Array.isArray(parsed) ? parsed : [value];
      } catch {
        return [value];
      }
    }
    return [""];
  };

   useEffect(() => {
    const fetchUser = async () => {
      try {
        const res = await fetch(`${BASE_URL}/get_user_details`, {
          method: "GET",
          credentials: "include",
        });
        if (!res.ok) throw new Error("Not logged in");
        const data = await res.json();
        setUser(data);
      } catch (err) {
        navigate("/checkout/login");
      }
    };
    fetchUser();
  }, [navigate]);


  /* ================= FETCH ITEMS ================= */
  const fetchItems = async (pageNo = 1) => {
    const res = await fetch(
      `${BASE_URL}/fetch_item_by_page?page_no=${pageNo}&page_size=${pageSize}`,{
        method: "POST",
        credentials: "include",
      }
    );
    const data = await res.json();
    setItems(data.items || []);
    setTotalCount(data.total_count || 0);
    setPage(data.current_page || pageNo);
  };

  useEffect(() => {
    if (!user) return;
    fetchItems(1);
  }, [user]);

  /* ================= HANDLERS ================= */
  const handleChange = (e) =>
    setForm({ ...form, [e.target.name]: e.target.value });

  /* ================= TAGS ================= */
  const addTag = () => {
    if (tagInput.trim()) {
      setTags([...tags, tagInput.trim()]);
      setTagInput("");
    }
  };
  
  const removeTag = (i) => setTags(tags.filter((_, idx) => idx !== i));

  /* ================= ARRAY HELPERS ================= */
  const updateArray = (arr, setArr, i, v) => {
    const copy = [...arr];
    copy[i] = v;
    setArr(copy);
  };
  const removeArray = (arr, setArr, i) =>
    setArr(arr.filter((_, idx) => idx !== i));


  const searchItems = async (query) => {
    if (!query) {
      // fetchItems(1);
      return;
    }

    try {
      const res = await fetch(`${BASE_URL}/search_items?query=${encodeURIComponent(query)}`, {
        method: "GET",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        }
      );
      const data = await res.json();

      if (!res.ok) return;

      setItems(data.message || []);
      setTotalCount(data.message?.length || 0);
      setIsSearching(true);
    } catch (err) {
      setMessage({ type:"err.message", text:"Search error"});
    }
  };

  useEffect(() => {
    const delay = setTimeout(() => {
      if (!searchQuery.trim()) {
        fetchItems(1);        // ✅ fetch all items
        setIsSearching(false);
      } else {
        searchItems(searchQuery);
      }
    }, 400);

    return () => clearTimeout(delay);
  }, [searchQuery]);          // ✅ REQUIRED


  
  /* ================= FILE UPLOAD ================= */
  const uploadFiles = async (itemId) => {
    if (!itemId) return;

    try {
      // === IMAGES ===
      if (images.length) {
        const fd = new FormData();
        images.forEach((img) => fd.append("files", img));

        const res = await fetch(`${BASE_URL}/upload_images/${itemId}`, {
          method: "POST",
          credentials: "include",
          body: fd,
        });

        if (!res.ok) {
          setMessage({ type: "error", text: "Image upload failed"});
        } else {
          const data = await res.json();
          // Replace blob previews with backend URLs
          setImageList(data.images.map((img) => ({
            image_id: img.image_id,
            file_name: img.file_name,
            image_url:`${BASE_URL}/download_image/${img.image_id}`,
          })));
        }
      }

      // === VIDEO ===
      if (videos.length) {
        const fd = new FormData();
        fd.append("file", videos[0]);

        await fetch(`${BASE_URL}/upload_videos/${itemId}`, {
          method: "POST",
          credentials: "include",
          body: fd,
        }); 

        setVideoUrl(`${BASE_URL}/download_video/item/${itemId}`);
      }

      // === ATTACHMENTS ===
      if (attachments.length) {
        const fd = new FormData();
        fd.append("file", attachments[0]);

        const res = await fetch(`${BASE_URL}/upload_attachments/${itemId}`, {
          method: "POST",
          credentials: "include",
          body: fd,
        });

        if (res.ok) {
          setAttachmentList([
            {
              id: itemId,
              name: attachments[0].name,
              url: `${BASE_URL}/download_attachment/item/${itemId}`,
            },
          ]);
        }
      }

      // Clear selected files after successful upload
      setImages([]);
      setVideos([]);
      setAttachments([]);
    } catch (err) {
      // console.error("Upload error:", err);
      setMessage({ type:"err.message", text:"Upload error"});
    }
  };


  /* ================= ADD ITEM ================= */
  const addItem = async () => {
    if (!form.gst_slabs_tax_class) {
      setMessage({
        type: "error",
        text: "Please select a GST class"
      });
      return;
    }

    const payload = {
      name: form.name || "",
      category: form.category || "",
      parent_category: form.parent_category || "",
      sku_no: form.sku_no || "",
      quantity: Number(form.quantity) || 0,
      price: Number(form.price) || 0,
      brand: form.brand || "",
      description: { text: form.description || "" },
      package_details: { box: form.package_details || "" },
      gst_slabs_tax_class: form.gst_slabs_tax_class,
      tags,
      features,
      specification: specifications,
      warranty_information,
    };

    try {
      const res = await fetch(`${BASE_URL}/add_item`, {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });

      const data = await res.json();

      if (!res.ok) {
        setMessage({ type:"error", text: data.error || "Failed to add item"});
        return;
      }

      await fetchItems(1);

      clearForm();
      setMessage({ type: "success", text:"Item added successfully!"});
    } catch (err) {
      setMessage({ type:"error", text:"Error adding item" });
    }
  };

  /* ================= MODIFY ITEM ================= */
  const modifyItem = async () => {
    if (!selectedItem) return;

    if (!form.gst_slabs_tax_class) {
      setMessage({
        type: "error",
        text: "Please select a GST class"
      });
      return;
    }

    const payload = {
      name: form.name || "",
      category: form.category || "",
      parent_category: form.parent_category || "",
      sku_no: form.sku_no || "",
      quantity: Number(form.quantity) || 0,
      price: Number(form.price) || 0,
      brand: form.brand || "",
      description: { text: form.description || "" },
      package_details: { box: form.package_details || "" },
      gst_slabs_tax_class: form.gst_slabs_tax_class,
      tags,
      features,
      specification: specifications,
      warranty_information,
    };

    try {
      const res = await fetch(`${BASE_URL}/update_item/${selectedItem.item_id}`, {
        method: "PUT",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });

      const data = await res.json();

      if (!res.ok) {
        setMessage({ type: "error", text: data.error || "Failed to update item"});
        return;
      }

      const itemId = selectedItem.item_id;

      // ✅ Upload files after modifying item
      await uploadFiles(itemId);
      await loadItem(selectedItem);
      
      fetchItems(page)  ;
      clearForm();
      setMessage({ type:"success", text:"Item updated successfully!"});
    } catch (err) {
      setAttachmentList({ type: "error", text: "Error updating item" });
    }
  };

  /* ================= DELETE ITEM ================= */
  const deleteItem = async () => {
    if (!selectedItem) {
      setMessage("Please select an item first");
      return;
    }

    const confirmDelete = window.confirm(
      `Are you sure you want to delete "${selectedItem.name}"?`
    );
    if (!confirmDelete) return;

    try {
      const res = await fetch(
        `${BASE_URL}/delete_item/${selectedItem.item_id}`,{
          method: "DELETE", 
          credentials: "include",
        }
      );

      const data = await res.json();

      if (!res.ok) {
        setMessage({ type:"error", text: data.error || "Failed to delete item" });
        return;
      }

      // ✅ safest way: refetch from backend
      fetchItems(page);

      clearForm();
      setMessage({ type: "success", text: "Item deleted successfully" });
    } catch (err) {
      setMessage({ type: "error", text: "Server error while deleting item" });
    }
  };


  /* ================= LOAD ITEM ================= */
  const loadItem = async (item) => {
    setSelectedItem(item);

    setForm({
      name: item.name || "",
      category: item.category || "",
      parent_category: item.parent_category || "",
      sku_no: item.sku_no || "",
      quantity: item.quantity || "",
      price: item.price || "",
      brand: item.brand || "",
      package_details: item.package_details?.box || "",
      description: item.description?.text || "",
      gst_slabs_tax_class: item.gst_slabs_tax_class || "",
    });

    setTags(item.tags || []);
    setFeatures(normalizeArray(item.features || [""]));
    setSpecifications(normalizeArray(item.specification || [""]));
    setWarrantyInformation(normalizeArray(item.warranty_information || [""]));

    setIsEditing(true);

    try {
      const imgRes = await fetch(`${BASE_URL}/download_images/${item.item_id}`,{
        method: "GET",
        credentials: "include",
      });
      const imgData = imgRes.ok ? await imgRes.json() : [];
      setImageList(
        imgData.map((img) => ({
          image_id: img.image_id,
          file_name: img.file_name,
          image_url: `${BASE_URL}/download_image/${img.image_id}`,
        }))
      );

      
      /* ================= LOAD VIDEO SAFELY ================= */
      let videoExists = false;
      try {
        const res = await fetch(
          `${BASE_URL}/download_video/item/${item.item_id}`,{
            method: "GET",
            credentials: "include",
          }
        );
        videoExists = res.ok;
      } catch {}

      setVideoUrl(
        videoExists ? `${BASE_URL}/download_video/item/${item.item_id}` : null
      );

      /* ================= LOAD ATTACHMENT SAFELY ================= */
      let attachmentExists = false;
      try {
        const res = await fetch(
          `${BASE_URL}/download_attachment/item/${item.item_id}`,{
            method: "GET",
            credentials: "include", 
          }
        );
        attachmentExists = res.ok;
      } catch {}

      setAttachmentList(
        attachmentExists
          ? [
              {
                id: item.item_id,
                name: "Download Attachment",
                url: `${BASE_URL}/download_attachment/item/${item.item_id}`,
              },
            ]
          : []
      );

      // Clear selected files
      setImages([]);
      setVideos([]);
      setAttachments([]);
    } 
    catch {
      setImageList([]);
      setVideoUrl(null);
      setAttachmentList([]);
    }
  };

  /* ================= CLEAR FORM ================= */
  const clearForm = () => {
    setForm({
      name: "",
      category: "",
      parent_category: "",
      sku_no: "",
      quantity: "",
      price: "",
      brand: "",
      package_details: "",
      description: "",
      gst_slabs_tax_class: "",
    });
    setTags([]);
    setFeatures([""]);
    setSpecifications([""]);
    setWarrantyInformation([""]);
    setImages([]);
    setVideos([]);
    setAttachments([]);
    setImageList([]);
    setVideoUrl(null);
    setAttachmentList([]);
    setSelectedItem(null);
    setIsEditing(false);
  };

  useEffect(() => {
    if (!message) return;

    const timer = setTimeout(() => {
      setMessage(null);
    }, 3000); // disappears after 3 seconds

    return () => clearTimeout(timer);
  }, [message]);


  /* ================= UI ================= */
  return (
    <div className="items-container">
      {/* LEFT PANEL */}
      <div className="items-list">

        <div className="search-wrapper">
          <input
            placeholder="Search items"
            className="search"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
          />

          {searchQuery && (
            <span
              className="clear-icon"
              onClick={() => setSearchQuery("")}
            >
              ✕
            </span>
          )}
        </div>


        {isSearching && (
          <small style={{ opacity: 0.6 }}>
            Search results for "{searchQuery}"
          </small>
        )}

        <h4>Items ({totalCount})</h4>
        {items.map((item) => (
            <div
              key={item.item_id}
              className={`item-card ${selectedItem?.item_id === item.item_id ? "active" : ""}`}
              onClick={(e) => {
                e.stopPropagation();   // ✅ IMPORTANT
                loadItem(item);
              }}
            >
            <h5>{item.name}</h5>
            <p>₹{item.price}</p>
            <span>{item.quantity > 0 ? "In stock" : "Out of stock"}</span>
          </div>
        ))}
        <Pagination page={page} total={totalCount} pageSize={pageSize} onPageChange={fetchItems} />
      </div>

      {/* RIGHT PANEL */}
      <div className="item-details">
        <div className="actions">
          <button onClick={addItem}>+ Add Item</button>
          <button disabled={!isEditing} onClick={modifyItem}>✏ Modify</button>
          <button disabled={!isEditing} className="danger" onClick={deleteItem}>🗑 Delete</button>
        </div>
        {message && (
          <p className={`msg ${message.type}`}>
            {message.text}</p>
        )}

        {/* FORM */}
        <div className="form-grid">
          <input name="name" placeholder="Name" value={form.name} onChange={handleChange} />
          <input name="category" placeholder="Category" value={form.category} onChange={handleChange} />
          <input name="parent_category" placeholder="Parent Category" value={form.parent_category} onChange={handleChange} />
          <input name="sku_no" placeholder="SKU No" value={form.sku_no} onChange={handleChange} />
          <input name="quantity" placeholder="Quantity" value={form.quantity} onChange={handleChange} />
          <input name="price" placeholder="Price" value={form.price} onChange={handleChange} />
          <input name="brand" placeholder="Brand" value={form.brand} onChange={handleChange} />
          <input name="package_details" placeholder="Package Details" value={form.package_details || ""} onChange={handleChange} />
          <select
            name="gst_slabs_tax_class"
            value={form.gst_slabs_tax_class}
            onChange={handleChange}
            required
          >
            <option value="">Select GST Class</option>
            <option value="A">A - 18%</option>
            <option value="B">B - 5%</option>
          </select>
        </div>

        <textarea name="description" placeholder="Description" value={form.description || ""} onChange={handleChange} />

        {/* TAGS */}
        <div className="form_container">
          
          <h4>Tags</h4>
          <div className="tags">
            {tags.map((t, i) => (
              <span key={i} onClick={() => removeTag(i)}>
                {t} ×
              </span>
            ))}
          </div>
          <input
            value={tagInput}
            placeholder="Add tag"
            onChange={(e) => setTagInput(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && addTag()}
          />

          {/* FEATURES / SPECIFICATIONS / WARRANTY */}
          <div className="section_container">

            {/* LEFT COLUMN – FEATURES */}
            <div className="left_column">
              <h4>Features</h4>
              {features.map((f, i) => (
                <div key={i} className="row">
                  <input
                    value={f}
                    onChange={(e) =>
                      updateArray(features, setFeatures, i, e.target.value)
                    }
                  />
                  <span onClick={() => removeArray(features, setFeatures, i)}>×</span>
                </div>
              ))}
              <button onClick={() => setFeatures([...features, ""])}>
                + Add Feature
              </button>
            </div>

            {/* RIGHT COLUMN – SPECIFICATIONS + WARRANTY */}
            <div className="right_column">

              <h4>Specifications</h4>
              {specifications.map((s, i) => (
                <div key={i} className="row">
                  <input
                    value={s}
                    onChange={(e) =>
                      updateArray(specifications, setSpecifications, i, e.target.value)
                    }
                  />
                  <span onClick={() => removeArray(specifications, setSpecifications, i)}>
                    ×
                  </span>
                </div>
              ))}
              <button onClick={() => setSpecifications([...specifications, ""])}>
                + Add Spec
              </button>

              <h4 style={{ marginTop: "20px" }}>Warranty</h4>
              {warranty_information.map((w, i) => (
                <div key={i} className="row">
                  <input
                    value={w}
                    onChange={(e) =>
                      updateArray(
                        warranty_information,
                        setWarrantyInformation,
                        i,
                        e.target.value
                      )
                    }
                  />
                  <span
                    onClick={() =>
                      removeArray(
                        warranty_information,
                        setWarrantyInformation,
                        i
                      )
                    }
                  >
                    ×
                  </span>
                </div>
              ))}
              <button onClick={() => setWarrantyInformation([...warranty_information, ""])}>
                + Add Warranty
              </button>

            </div>
          </div>
        </div>

        {/* ================= FILE UPLOADS ================= */}
        <h4>Images *</h4>

        <div
          className="drop-box"
          onDragOver={(e) => e.preventDefault()}
          onDrop={(e) => {
            e.preventDefault();
            setImages([...images, ...Array.from(e.dataTransfer.files)]);
          }}
        >
          Drag & drop images here or{" "}
          <label className="browse">
            Browse
            <input
              type="file"
              hidden
              multiple
              accept="image/*"
              onChange={(e) =>
                setImages([...images, ...Array.from(e.target.files)])
              }
            />
          </label>
        </div>

        {/* Selected image previews */}
        <div className="image-preview-row">
          {images.map((img, i) => (
            <div key={i} className="image-thumb">
              <img src={URL.createObjectURL(img)} alt="" />
              <span onClick={() => setImages(images.filter((_, x) => x !== i))}>
                ×
              </span>
            </div>
          ))}

          {/* + button */}
          <label className="image-add">
            +
            <input
              type="file"
              hidden
              multiple
              accept="image/*"
              onChange={(e) =>
                setImages([...images, ...Array.from(e.target.files)])
              }
            />
          </label>

          {imageList.length > 0 && (
          <>
            <div className="image-preview">
              {imageList.map((img) => (
                <img 
                  key={img.image_id} 
                  src={img.image_url} 
                  width="120"
                  alt="" 
                />
              ))}
            </div>
          </>
        )}
        </div>

        {/* VIDEO */}
        
        <h4>Video *</h4>

        <div
          className="drop-box"
          onDragOver={(e) => e.preventDefault()}
          onDrop={(e) => {
            e.preventDefault();
            setVideos([e.dataTransfer.files[0]]);
          }}
        >
          Drag & drop video here or{" "}
          <label className="browse">
            Browse
            <input
              type="file"
              hidden
              accept="video/*"
              onChange={(e) => setVideos([e.target.files[0]])}
            />
          </label>
        </div>

        {/* Selected video preview */}
        {videos.length > 0 && (
          <div className="video-preview">
            🎬 {videos[0].name}
            <span onClick={() => setVideos([])}>×</span>
          </div>
        )}

        {/* Uploaded video */}
        {videoUrl && (
          <div className="video-preview">
            <video width="240"   style={{ marginTop: "10px" }} controls src={videoUrl}  />
          </div>
        )}


        {/* ATTACHMENTS */}
       
        <h4>Attachment *</h4>

        <div
          className="drop-box"
          onDragOver={(e) => e.preventDefault()}
          onDrop={(e) => {
            e.preventDefault();
            setAttachments([e.dataTransfer.files[0]]);
          }}
        >
          Drag & drop attachment here or{" "}
          <label className="browse">
            Browse
            <input
              type="file"
              hidden
              accept=".pdf,.doc,.docx"
              onChange={(e) => setAttachments([e.target.files[0]])}
            />
          </label>
        </div>

        {/* Selected attachment */}
        {attachments.length > 0 && (
          <div className="file-chip">
            📎 {attachments[0].name}
            <span onClick={() => setAttachments([])}>×</span>
          </div>
        )}

        {/* Uploaded attachments */}
        {attachmentList.length > 0 && (
          <div className="attachment-list">
            {attachmentList.map((a) => (
              <div key={a.id}>
                <a href={a.url} target="_blank" rel="noreferrer">
                  📄 {a.name}
                </a>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default ItemsPage;